import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { User, Mail, Phone, Building2, Calendar, Shield, Wallet, Edit, Loader2 } from 'lucide-react';
import { PageHeader } from '../components/common/PageHeader';
import { StatusBadge } from '../components/common/StatusBadge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { Badge } from '../components/ui/badge';
import { format } from 'date-fns';
import { pt } from 'date-fns/locale';
import { getEmployee } from '../api-clients';
import { EmployeeDto } from '../web-api-client';
import { Employee, RestrictionLevel } from '../types';
import { RestrictionBadge } from '../components/common/RestrictionBadge';
import { useToast } from '../hooks/use-toast';
import { getErrorToast } from '../lib/error-utils';

// Helper function to map EmployeeDto to Employee
function mapEmployeeDtoToEmployee(employee: EmployeeDto): Employee {
  return {
    id: employee.id?.toString() || '',
    name: employee.fullName || '',
    email: employee.email || '',
    phone: employee.phoneNumber || '',
    role: employee.role,
    companyId: employee.companyId?.toString() || '',
    companyName: employee.companyName || 'N/A',
    status: employee.isActive ? 'active' : 'inactive',
    createdAt: employee.createdAt ? new Date(employee.createdAt).toISOString() : new Date().toISOString(),
    walletConfigured: !!employee.walletAddress,
    restrictionLevel: (employee.restrictionLevel ?? 0) as RestrictionLevel,
  };
}

export default function EmployeeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [employee, setEmployee] = useState<Employee | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadEmployee = async () => {
      if (!id) {
        setError('ID do colaborador não fornecido');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const employeeId = parseInt(id, 10);
        if (isNaN(employeeId)) {
          throw new Error('ID inválido');
        }
        const employeeDto = await getEmployee(employeeId);
        setEmployee(mapEmployeeDtoToEmployee(employeeDto));
      } catch (err) {
        const errorToast = getErrorToast(err, 'Erro', 'Erro ao carregar colaborador');
        setError(errorToast.description);
        toast({
          title: errorToast.title,
          description: errorToast.description,
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    };

    loadEmployee();
  }, [id, toast]);

  const roleLabels: Record<string, string> = {
    CompanyAdministrator: 'Administrador',
    CompanyManager: 'Gestor',
    CompanyDispatcher: 'Expedidor',
    CompanyEmployee: 'Colaborador',
    CompanyDriver: 'Motorista',
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[50vh]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !employee) {
    return (
      <div className="flex items-center justify-center h-[50vh]">
        <p className="text-muted-foreground">{error || 'Colaborador não encontrado'}</p>
      </div>
    );
  }

  // Note: eCMRs are not yet available from the backend, so we'll show a placeholder
  const employeeEcmrs: any[] = [];

  return (
    <div className="space-y-6">
      <PageHeader
        title={employee.name}
        description={`${roleLabels[employee.role]} • ${employee.companyName}`}
        backLink="/employees"
        actions={
          <Button onClick={() => navigate(`/employees/${id}/edit`)}>
            <Edit className="h-4 w-4 mr-2" />
            Editar
          </Button>
        }
      />

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                Informações do Colaborador
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-start gap-6">
                <div className="flex h-20 w-20 items-center justify-center rounded-full bg-primary/10">
                  <User className="h-10 w-10 text-primary" />
                </div>
                <div className="flex-1 grid gap-4 sm:grid-cols-2">
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground flex items-center gap-2">
                      <Mail className="h-4 w-4" />
                      Email
                    </p>
                    <p className="font-medium">{employee.email}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground flex items-center gap-2">
                      <Phone className="h-4 w-4" />
                      Telefone
                    </p>
                    <p className="font-medium">{employee.phone}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground flex items-center gap-2">
                      <Building2 className="h-4 w-4" />
                      Empresa
                    </p>
                    <p className="font-medium">{employee.companyName}</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm text-muted-foreground flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      Data de Registo
                    </p>
                    <p className="font-medium">
                      {format(new Date(employee.createdAt), "d 'de' MMMM, yyyy", { locale: pt })}
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>eCMRs Recentes</CardTitle>
            </CardHeader>
            <CardContent>
              {employeeEcmrs.length > 0 ? (
                <div className="space-y-3">
                  {employeeEcmrs.map((ecmr) => (
                    <div
                      key={ecmr.id}
                      className="flex items-center justify-between p-3 rounded-lg bg-muted/50 hover:bg-muted cursor-pointer transition-colors"
                      onClick={() => navigate(`/ecmrs/${ecmr.id}`)}
                    >
                      <div>
                        <p className="font-medium">{ecmr.number}</p>
                        <p className="text-sm text-muted-foreground">
                          {ecmr.senderName} → {ecmr.consigneeName}
                        </p>
                      </div>
                      <StatusBadge status={ecmr.status} />
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-8">
                  Sem eCMRs associados
                </p>
              )}
            </CardContent>
          </Card>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5" />
                Estado e Função
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Estado</span>
                <Badge variant={employee.status === 'active' ? 'active' : 'inactive'}>
                  {employee.status === 'active' ? 'Ativo' : 'Inativo'}
                </Badge>
              </div>
              <Separator />
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Função</span>
                <Badge variant="secondary">{roleLabels[employee.role]}</Badge>
              </div>
              <Separator />
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Nível de Restrição</span>
                <RestrictionBadge level={employee.restrictionLevel ?? 0} />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Wallet className="h-5 w-5" />
                Wallet
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Estado</span>
                <Badge variant={employee.walletConfigured ? 'active' : 'pending'}>
                  {employee.walletConfigured ? 'Configurada' : 'Pendente'}
                </Badge>
              </div>
              {!employee.walletConfigured && (
                <p className="text-xs text-muted-foreground">
                  A wallet será configurada no primeiro login do colaborador
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
